import { useQuery } from '@tanstack/react-query';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import { StarRating } from '@/components/ui/star-rating';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageCard,
  ListPageStats,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { clientReviewApi, queryFn, type ClientReview } from '@/lib/api';
import { Star, Settings } from 'lucide-react';
import { format } from 'date-fns';

function ReviewsContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();

  const { data: reviews = [], isLoading: loading } = useQuery({
    queryKey: ['reviews', currentBusiness?.id],
    queryFn: () => queryFn(clientReviewApi.list(currentBusiness!.id)),
    enabled: !!currentBusiness,
  });

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const columns: ColumnDef<ClientReview>[] = [
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.client_name}</div>
        </div>
      ),
    },
    {
      accessorKey: 'invoice_number',
      header: 'Invoice',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.invoice_number || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'rating',
      header: ({ column }) => <SortableHeader column={column} title="Rating" />,
      cell: ({ row }) => (
        <StarRating value={row.original.rating} readonly size="sm" />
      ),
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      cell: ({ row }) => {
        const comments = row.original.comments;
        if (!comments) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-muted-foreground line-clamp-2 max-w-xs">
            {comments}
          </span>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
  ];

  // Reviews disabled state
  if (!loading && currentBusiness && !currentBusiness.client_reviews_enabled) {
    return (
      <div className="px-6 pt-6 pb-6">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Star className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Client reviews are not enabled</h2>
          <p className="text-muted-foreground mb-6 max-w-sm">
            Enable client reviews to allow your customers to leave feedback after paying invoices.
          </p>
          <Button onClick={() => nav('/settings/client-reviews')}>
            <Settings className="mr-2 h-4 w-4" />
            Enable in Settings
          </Button>
        </div>
      </div>
    );
  }

  // Calculate average rating
  const averageRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  return (
    <ListPage>
      <ListPageHeader>
        <ListPageTitle>Reviews</ListPageTitle>
      </ListPageHeader>

      <ListPageCard>
        <ListPageStats>
          <div>
            <div className="text-sm text-muted-foreground mb-1">Total Reviews</div>
            <div className="text-2xl font-medium">{reviews.length}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-1">Average Rating</div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-medium">{averageRating.toFixed(1)}</span>
              <StarRating value={Math.round(averageRating)} readonly size="sm" />
            </div>
          </div>
        </ListPageStats>

        <ListPageContent>
          <DataTable
            columns={columns}
            data={reviews}
            loading={loading}
            noBorder
            onRowClick={(review) => nav(`/reviews/${review.id}`)}
            emptyIcon={<Star className="h-6 w-6 text-muted-foreground" />}
            emptyTitle="No reviews yet"
            emptyDescription="Reviews will appear here when clients submit feedback."
          />
        </ListPageContent>
      </ListPageCard>
    </ListPage>
  );
}

export function Reviews() {
  return <ReviewsContent />;
}
